import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Checkbox, FormControlLabel } from "@material-ui/core";
import VisibilityOffRoundedIcon from "@material-ui/icons/VisibilityOffRounded";
import VisibilityRoundedIcon from "@material-ui/icons/VisibilityRounded";
import { GridRenderCellParams } from "@mui/x-data-grid";

import {
  addExportButton,
  fromStringToNumberParser,
  numberFilterOperators,
  tableCellRender,
} from "../../util/tableUtils";
import { DataGridCystellarTable } from "../../util/DataGridCystellarTable";
import httpClient from "../../../lib/httpClient";

type EmergencyEventAoiListProps = {
  eventId: string;
  aoiList: TerraShieldEventAoi[];
  selectAoiList: Function;
};

type AoiStatsEntity = {
  nr_buildings: number;
  sum_of_tiv: number;
};

type AoiStats = {
  buildings_in_aoi: AoiStatsEntity;
  claims_in_aoi: AoiStatsEntity;
  claims_flood: AoiStatsEntity;
  policies_flood: AoiStatsEntity;
};

type TerraShieldEventAoiWithStats = AoiStats & TerraShieldEventAoi & { selected: boolean };

export const EmergencyEventAoiList: React.FC<EmergencyEventAoiListProps> = ({
  eventId,
  aoiList,
  selectAoiList,
}) => {
  const { t } = useTranslation();

  const [aoiData, setAoiData] = useState<TerraShieldEventAoiWithStats[]>([]);
  const [isAoiDataFetching, setIsAoiDataFetching] = useState<boolean>(false);

  const getAoisStats = useCallback(
    async (aoiList: TerraShieldEventAoi[]) => {
      setIsAoiDataFetching(true);

      const result = await Promise.all(
        aoiList.map((aoi) => {
          return httpClient().get(`/terrashield/event/stats?eventId=${eventId}&aoiNumber=${aoi.number}`);
        }),
      );
      const statsOnly = result.map((res) => res.data);

      const aoiWithStats: TerraShieldEventAoiWithStats[] = aoiList.map((aoi) => {
        const currAoiStats = statsOnly.find((stats) => stats.aoiNumber === aoi.number);

        return { ...currAoiStats, ...aoi } as TerraShieldEventAoiWithStats;
      });

      setAoiData(aoiWithStats);
    },
    [eventId],
  );

  useEffect(() => {
    getAoisStats(aoiList);
    setIsAoiDataFetching(false);
  }, [getAoisStats, aoiList]);

  const onCheckboxChange = async (selectedRow: TerraShieldEventAoiWithStats) => {
    const updatedAoiData = aoiData.map((aoi) => {
      return aoi.number === selectedRow.number ? { ...aoi, selected: !selectedRow.selected } : { ...aoi };
    });

    const selected = updatedAoiData.filter((aoi) => aoi.selected);

    selectAoiList(selected);
    setAoiData(updatedAoiData);
  };

  const aoiColumns = [
    {
      field: "selected",
      headerName: t("policy.action"),
      sortable: false,
      filterable: false,
      renderCell: (params: { row: TerraShieldEventAoiWithStats }) => {
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <FormControlLabel
              label={""}
              control={
                <Checkbox
                  icon={<VisibilityOffRoundedIcon />}
                  checkedIcon={<VisibilityRoundedIcon />}
                  checked={!!params.row.selected}
                  onChange={(event) => onCheckboxChange(params.row)}
                  color="primary"
                />
              }
            />
          </div>
        );
      },
    },
    {
      field: "name",
      headerName: t("emergencyMonitoring.monitoringRegion"),
      sortable: false,
    },
    {
      field: "buildings_in_aoi.nr_buildings",
      headerName: t("emergencyMonitoring.numberOfPoliciesInAois"),
      valueGetter: (params: { row: TerraShieldEventAoiWithStats }) =>
        params.row.buildings_in_aoi.nr_buildings,
      valueParser: fromStringToNumberParser,
      filterOperators: numberFilterOperators,
      align: "right",
      headerAlign: "right",
      sortable: true,
    },
    {
      field: "buildings_in_aoi.sum_of_tiv",
      headerName: t("emergencyMonitoring.tivPoliciesMonitoringArea"),
      valueGetter: (params: { row: TerraShieldEventAoiWithStats }) => params.row.buildings_in_aoi.sum_of_tiv,
      valueParser: fromStringToNumberParser,
      valueFormatter: (params: GridRenderCellParams) => tableCellRender(Math.round(params.value)),
      filterOperators: numberFilterOperators,
      align: "right",
      headerAlign: "right",
      sortable: true,
    },
    {
      field: "policies_flood.nr_buildings",
      headerName: t("emergencyMonitoring.numberOfPoliciesInObservedArea"),
      valueGetter: (params: { row: TerraShieldEventAoiWithStats }) => params.row.policies_flood.nr_buildings,
      valueParser: fromStringToNumberParser,
      filterOperators: numberFilterOperators,
      align: "right",
      headerAlign: "right",
      sortable: true,
    },
    {
      field: "policies_flood.sum_of_tiv",
      headerName: t("emergencyMonitoring.tivPoliciesObservedArea"),
      valueGetter: (params: { row: TerraShieldEventAoiWithStats }) => params.row.policies_flood.sum_of_tiv,
      valueParser: fromStringToNumberParser,
      valueFormatter: (params: GridRenderCellParams) => tableCellRender(Math.round(params.value)),
      filterOperators: numberFilterOperators,
      align: "right",
      headerAlign: "right",
      sortable: true,
    },
  ];

  const columns = aoiColumns.map((tableColumn) => ({
    valueFormatter: (params: any) => tableCellRender(params.value),
    flex: 1,
    ...tableColumn,
  }));

  return (
    <Grid>
      <DataGridCystellarTable
        columns={columns}
        rows={aoiData}
        rowCount={aoiData.length}
        getRowId={(rowData: TerraShieldEventAoi) => rowData.number}
        isLoading={isAoiDataFetching}
        customToolbarComp={true}
        customToolbarWidgets={addExportButton(`Zone_di_Interesse_${eventId}`)}
      />
    </Grid>
  );
};
